import { AxiosError } from "axios";
import { useQuery } from "react-query";
import _keyBy from "lodash/keyBy";
import _map from "lodash/map";
import { useErrorBoundary } from "component-library";
import {
  FacetCategory,
  GET_FACETS_CATEGORY_QUERY_KEY,
  GET_LOCATIONS_BY_ID,
  getFacetsCategories,
  IGetFacetsResponse,
  getGoogleLocationsByIds,
} from "shared-services";
import { useTranslation } from "i18n";
import { authAxios as axios } from "shared-auth";
import { HALManager, UserManager } from "core";
import { BasicInfo } from "models";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { useGetPublicProfile } from "pages/Profile/hooks/useGetPublicProfile";
import {
  BasicInfoConverter,
  FacetConverter,
  IBasicInfoConverterContent,
} from "pages/MainPage/components/BasicInfoSection/converters";
import { getApiUrl } from "services/UrlBuilder";
import { IBasicInfoViewModel } from "pages/MainPage/components/BasicInfoSection/viewModels";

const GET_BASIC_INFO_QUERY_KEY = "GET_BASIC_INFO_QUERY_KEY";

export function useGetBasicInfo() {
  const { t } = useTranslation();
  const captureError = useErrorBoundary();
  const { isPublicView } = usePublicView();
  const { data: publicProfileData, isLoading: isPublicProfileLoading } =
    useGetPublicProfile();

  const { data: facets = [], isLoading: isFacetsLoading } = useQuery<
    IGetFacetsResponse,
    AxiosError
  >(
    GET_FACETS_CATEGORY_QUERY_KEY,
    async () => {
      const { data } = await getFacetsCategories(Object.values(FacetCategory));

      return data;
    },
    {
      onError: captureError,
    }
  );

  const { data: privateBasicInfoData, isLoading: isPrivateBasicInfoLoading } =
    useQuery<BasicInfo, AxiosError>(
      GET_BASIC_INFO_QUERY_KEY,
      async () => {
        const { data } = await axios.get(
          getApiUrl(HALManager.getBasicInfoUrl(UserManager.userId))
        );

        return data;
      },
      {
        enabled: !isPublicView,
        cacheTime: 0,
        onError: captureError,
      }
    );

  const basicInfoData = isPublicView
    ? publicProfileData?.basicInfo
    : privateBasicInfoData;

  const locations = basicInfoData?.locations || [];

  const locationIds: string[] = locations.map(
    // @ts-expect-error data discrepancy between private and public profile location (but it's the same)
    ({ googlePlaceId, locationId }) => googlePlaceId || locationId
  );

  const { data: googleLocationsData, isLoading: googleLocationsLoading } =
    useQuery(
      [GET_LOCATIONS_BY_ID, locationIds],
      () => getGoogleLocationsByIds(locationIds),
      {
        enabled: !!locationIds.length,
      }
    );

  const dataById = _keyBy(googleLocationsData, "googlePlaceId");

  const mergedLocations = _map(locations, (loc) => ({
    // @ts-expect-error data discrepancy between private and public profile location (but it's the same)
    ...loc,
    // @ts-expect-error same
    ...dataById[loc?.googlePlaceId || loc?.locationId],
  }));

  const facetsView = FacetConverter.convertToView(facets);

  const isLoading =
    isPublicProfileLoading ||
    isFacetsLoading ||
    isPrivateBasicInfoLoading ||
    googleLocationsLoading;

  // postRelease: the converter should be refactored and covered by tests
  const basicInfo =
    basicInfoData && mergedLocations && !isLoading
      ? BasicInfoConverter.convertToView(
          { ...basicInfoData, locations: mergedLocations },
          facetsView,
          {
            detailsYesMark: t("common:label.yes"),
            detailsNoMark: t("common:label.no"),
            detailsConnectorTo: t("common:label.to.lower"),
            detailsYears: t("common:label.years"),
            detailsBeltTo: t("common:label.beltTo"),
            detailsStones: t("common:label.stones.short"),
            detailsPounds: t("common:label.pounds.short"),
          } as IBasicInfoConverterContent,
          t,
          isPublicView
        )
      : ({} as IBasicInfoViewModel);

  return {
    basicInfo,
    isLoading,
    basicInfoConfiguration: {
      isVisibleActualAge: basicInfoData?.isVisibleActualAge,
      isVisibleDisability: basicInfoData?.isVisibleDisability,
      isVisibleGender: basicInfoData?.isVisibleGender,
      isVisibleHeritageAndEthnicity:
        basicInfoData?.isVisibleHeritageAndEthnicity,
      isVisibleVisasAndPermits: basicInfoData?.isVisibleVisasAndPermits,
    },
  };
}
